"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useProduct = exports.useProductVariantGroups = exports.useProductPrice = void 0;
const react_1 = require("react");
const context_1 = require("../context");
const helpers_1 = require("../helpers");
const variant_1 = require("../helpers/variant");
const constant_1 = require("../constant");
const useProductPrice = (product, variant) => {
    const { price, listingPrice } = (0, react_1.useMemo)(() => (0, helpers_1.getProductPrice)(product, variant), [product, variant]);
    return { price, listingPrice };
};
exports.useProductPrice = useProductPrice;
const useProductVariantGroups = (product, defaultVariantId) => {
    var _a, _b;
    const { variantGroups: productVariantGroup, variants } = product || {};
    //filterout disabled variants
    const allVariants = ((_a = variants === null || variants === void 0 ? void 0 : variants.items) === null || _a === void 0 ? void 0 : _a.filter((variant) => variant.status === "ENABLED")) || [];
    const { variantGroupIds, variantGroupOptionIds } = allVariants === null || allVariants === void 0 ? void 0 : allVariants.reduce((acc, v) => {
        const productVariantOptionIds = (v === null || v === void 0 ? void 0 : v.productVariantOptionIds) || [];
        productVariantOptionIds === null || productVariantOptionIds === void 0 ? void 0 : productVariantOptionIds.map((opt) => {
            if (!acc.variantGroupIds.includes(opt === null || opt === void 0 ? void 0 : opt.variantGroupId)) {
                acc.variantGroupIds.push(opt === null || opt === void 0 ? void 0 : opt.variantGroupId);
            }
            if (!acc.variantGroupOptionIds.includes(opt === null || opt === void 0 ? void 0 : opt.variantGroupOptionId)) {
                acc.variantGroupOptionIds.push(opt === null || opt === void 0 ? void 0 : opt.variantGroupOptionId);
            }
        });
        return acc;
    }, {
        variantGroupIds: [],
        variantGroupOptionIds: [],
    });
    //filterout disabled variantGroups having variants disabled
    const enabledProductVariantGroups = (_b = productVariantGroup === null || productVariantGroup === void 0 ? void 0 : productVariantGroup.filter((i) => variantGroupIds.includes(i.variantGroupId))) === null || _b === void 0 ? void 0 : _b.map((item) => {
        var _a;
        return Object.assign(Object.assign({}, item), { variantGroupOptionIds: (_a = item === null || item === void 0 ? void 0 : item.variantGroupOptionIds) === null || _a === void 0 ? void 0 : _a.filter((optId) => variantGroupOptionIds.includes(optId)) });
    });
    const defaultVariant = defaultVariantId
        ? allVariants.filter((v) => v.id === defaultVariantId)[0]
        : (0, helpers_1.getFirstVariantByInventory)(allVariants);
    const variantGroups = (0, context_1.useVariantGroups)();
    const [selectedVariant, setVariant] = (0, react_1.useState)(null);
    (0, react_1.useEffect)(() => {
        if (defaultVariant) {
            setVariant(defaultVariant);
        }
    }, [defaultVariant === null || defaultVariant === void 0 ? void 0 : defaultVariant.id, product === null || product === void 0 ? void 0 : product.id]);
    const currentVariantGroups = (0, react_1.useMemo)(() => {
        const { groups: currGroups } = (enabledProductVariantGroups || []).reduce((acc, pg, idx) => {
            var _a;
            const { lastSelectedOption, groups } = acc;
            const g = variantGroups === null || variantGroups === void 0 ? void 0 : variantGroups.find((v) => v.id === pg.variantGroupId);
            const vo = selectedVariant === null || selectedVariant === void 0 ? void 0 : selectedVariant.productVariantOptionIds.find((v) => v.variantGroupId === pg.variantGroupId);
            if (g) {
                const existVariantGroupOptions = (_a = pg.variantGroupOptionIds) === null || _a === void 0 ? void 0 : _a.map((id) => { var _a; return (_a = g.variantOptions) === null || _a === void 0 ? void 0 : _a.find((option) => option.id === id); });
                const opts = existVariantGroupOptions.map((o) => {
                    let isActive = !lastSelectedOption;
                    if (lastSelectedOption) {
                        const applicalbleVariants = allVariants.filter((v) => v.productVariantOptionIds.some((x) => x.variantGroupOptionId === lastSelectedOption));
                        isActive = applicalbleVariants.some((v) => v.productVariantOptionIds.some((x) => x.variantGroupOptionId === (o === null || o === void 0 ? void 0 : o.id)));
                    }
                    const isLastGroup = idx === ((enabledProductVariantGroups === null || enabledProductVariantGroups === void 0 ? void 0 : enabledProductVariantGroups.length) || 0) - 1;
                    if (isLastGroup) {
                        const price = (0, variant_1.getVariantPrice)(allVariants, [
                            ...groups,
                            Object.assign(Object.assign({}, g), { variantOptions: [o] }),
                        ]);
                        const listingPrice = (0, variant_1.getVariantListingPrice)(allVariants, [
                            ...groups,
                            Object.assign(Object.assign({}, g), { variantOptions: [o] }),
                        ]);
                        const status = (0, variant_1.getVariantStatus)(allVariants, [
                            ...groups,
                            Object.assign(Object.assign({}, g), { variantOptions: [o] }),
                        ]);
                        const position = (0, variant_1.getVariantPosition)(allVariants, [
                            ...groups,
                            Object.assign(Object.assign({}, g), { variantOptions: [o] }),
                        ]);
                        const title = (0, variant_1.getVariantTitle)(allVariants, [
                            ...groups,
                            Object.assign(Object.assign({}, g), { variantOptions: [o] }),
                        ]);
                        const images = (0, variant_1.getVariantImages)(allVariants, [
                            ...groups,
                            Object.assign(Object.assign({}, g), { variantOptions: [o] }),
                        ]);
                        const minimumOrderQuantity = (0, variant_1.getVariantMinimumOrderQuantity)(allVariants, [...groups, Object.assign(Object.assign({}, g), { variantOptions: [o] })]);
                        const maximumOrderQuantity = (0, variant_1.getVariantMaximumOrderQuantity)(allVariants, [...groups, Object.assign(Object.assign({}, g), { variantOptions: [o] })]);
                        const thumbImage = (0, variant_1.getVariantThumbImage)(allVariants, [
                            ...groups,
                            Object.assign(Object.assign({}, g), { variantOptions: [o] }),
                        ]);
                        const marketPlaceLinks = (0, variant_1.getVariantMarketPlaceLinks)(allVariants, [
                            ...groups,
                            Object.assign(Object.assign({}, g), { variantOptions: [o] }),
                        ]);
                        return Object.assign(Object.assign({}, o), { active: isActive, selected: (vo === null || vo === void 0 ? void 0 : vo.variantGroupOptionId) === (o === null || o === void 0 ? void 0 : o.id), price,
                            listingPrice,
                            status,
                            position,
                            title,
                            images,
                            minimumOrderQuantity,
                            maximumOrderQuantity,
                            thumbImage,
                            marketPlaceLinks, tag: listingPrice > price ? `Save ₹${listingPrice - price}` : null });
                    }
                    return Object.assign(Object.assign({}, o), { active: isActive, selected: (vo === null || vo === void 0 ? void 0 : vo.variantGroupOptionId) === (o === null || o === void 0 ? void 0 : o.id), price: null, listingPrice: null, status: null, tag: null, title: null, images: null, minimumOrderQuantity: null, maximumOrderQuantity: null });
                });
                return {
                    lastSelectedOption: vo === null || vo === void 0 ? void 0 : vo.variantGroupOptionId,
                    groups: [...groups, Object.assign(Object.assign({}, g), { variantOptions: opts })],
                };
            }
            return acc;
        }, { lastSelectedOption: null, groups: [] });
        return currGroups;
    }, [!!product, !!variantGroups, selectedVariant]);
    const changeVariantGroupOption = (variantGroupId, variantGroupOptionId) => {
        if (selectedVariant) {
            const { productVariantOptionIds } = selectedVariant;
            const { groups } = productVariantOptionIds.reduce((acc, opts) => {
                var _a;
                const here = opts.variantGroupId === variantGroupId;
                let newLastSelectedOption = here
                    ? variantGroupOptionId
                    : opts.variantGroupOptionId;
                if (acc.change && acc.lastSelectedOption) {
                    const applicalbleVariants = allVariants.filter((v) => v.productVariantOptionIds.some((x) => x.variantGroupOptionId === acc.lastSelectedOption));
                    const firstVariant = applicalbleVariants.find((v) => v.productVariantOptionIds.some((x) => x.variantGroupId === opts.variantGroupId));
                    newLastSelectedOption = (_a = firstVariant === null || firstVariant === void 0 ? void 0 : firstVariant.productVariantOptionIds.find((x) => x.variantGroupId === opts.variantGroupId)) === null || _a === void 0 ? void 0 : _a.variantGroupOptionId;
                }
                return {
                    change: acc.change || here,
                    groups: [
                        ...acc.groups,
                        {
                            variantGroupId: opts.variantGroupId,
                            variantGroupOptionId: newLastSelectedOption,
                        },
                    ],
                    lastSelectedOption: newLastSelectedOption,
                };
            }, {
                lastSelectedOption: null,
                change: false,
                groups: [],
            });
            const newVariant = allVariants.find((v) => v.productVariantOptionIds.every((x) => groups.some((g) => g.variantGroupId === x.variantGroupId &&
                g.variantGroupOptionId === x.variantGroupOptionId)));
            if (newVariant) {
                setVariant(newVariant);
            }
        }
    };
    return [selectedVariant, currentVariantGroups, changeVariantGroupOption];
};
exports.useProductVariantGroups = useProductVariantGroups;
const useProduct = (product, variantId) => {
    var _a, _b, _c;
    if (!product)
        return null;
    const blockInventory = (0, context_1.useConfiguration)(constant_1.BLOCK_INVENTORY, 0);
    const variant = (_b = (_a = product.variants) === null || _a === void 0 ? void 0 : _a.items) === null || _b === void 0 ? void 0 : _b.find((v) => v.id === variantId);
    const thumbImage = (0, helpers_1.getThumbImage)(product, variant);
    const discount = (0, helpers_1.getProductDiscount)(product, variant);
    const { hasInventory, currentInventory } = (0, helpers_1.getProductInventory)(product, variantId, blockInventory);
    if (!variantId) {
        return Object.assign(Object.assign({}, product), { id: product.id, title: product.title, images: product === null || product === void 0 ? void 0 : product.images, hasInventory,
            currentInventory,
            thumbImage,
            discount });
    }
    return Object.assign(Object.assign(Object.assign({}, product), variant), { id: product.id, title: product.title, images: {
            items: [...(((_c = variant === null || variant === void 0 ? void 0 : variant.images) === null || _c === void 0 ? void 0 : _c.items) || [])],
        }, hasInventory,
        currentInventory,
        thumbImage,
        discount });
};
exports.useProduct = useProduct;
